/**
 * How the pet is presented in a battle and in the hub (Kevin, 2026-10-14: "barely visible on the screen"), as tunables
 * to compare: (A) drawn larger than life, (B) a soft gold marker on the ground under it, (C) each bark answered with a
 * "WOOF!" shout and a gold mote flying to the troop it mustered, (D) its portrait on the battle HUD. The defaults are
 * PET_PRESENT; `?petlook=` overrides them for side-by-sides (`none` for the plain pup, else any of the letters `abcd`,
 * e.g. `?petlook=ab`; `?petscale=1.3` sets the battle scale on its own). Presentation only.
 */
export interface PetLook {
  /** Battle scale (1 = life size: about a troop's knee), and the hub's. */
  readonly scale: number;
  readonly hubScale: number;
  readonly marker: boolean;
  readonly burst: boolean;
  readonly hud: boolean;
}

export const PET_PRESENT: PetLook & {
  /** How much the spots round the formation (petMind.ts petSpot) grow with the scale: 0 none, 1 in proportion. */
  readonly spotGrow: number;
  /** Life size: what `a` off draws at, battle and hub. */
  readonly lifeScale: number; readonly lifeHubScale: number;
} = {
  scale: 1.6, hubScale: 2, marker: true, burst: true, hud: true,
  spotGrow: 0.35, lifeScale: 1, lifeHubScale: 1.5,
};

/** The look a `?petlook=` / `?petscale=` query asks for (the defaults when it asks nothing). Pure. */
export function petLookFrom(query: string): PetLook {
  const q = new URLSearchParams(query), look = q.get("petlook");
  const has = (c: string) => look === null || (look !== "none" && look.includes(c));
  const scale = Number(q.get("petscale"));
  return {
    scale: Number.isFinite(scale) && scale > 0 ? scale : has("a") ? PET_PRESENT.scale : PET_PRESENT.lifeScale,
    hubScale: has("a") ? PET_PRESENT.hubScale : PET_PRESENT.lifeHubScale,
    marker: has("b"), burst: has("c"), hud: has("d"),
  };
}

let cached: PetLook | null = null;
/** The look in force (read once from the page's query). */
export function petLook(): PetLook {
  cached ??= typeof location === "undefined" ? petLookFrom("") : petLookFrom(location.search);
  return cached;
}

/** How far the spots round the formation reach for a pet drawn at `scale` (a multiplier on PET_TUNING's offsets). */
export function spotReach(scale: number): number { return 1 + (scale - 1) * PET_PRESENT.spotGrow; }
